<template>
  <AdaptiveSheet
    v-model:opened="popupOpened"
    class="product-detail-popup"
    swipe-to-close
    close-on-escape
    :tablet-fullscreen="false"
    @closed="onClosed"
  >
    <f7-page class="product-detail-page">
      <!-- Gallery header -->
      <div class="gallery-wrap">
        <f7-swiper v-if="images.length" pagination :params="{ loop: images.length > 1 }">
          <f7-swiper-slide v-for="img in images" :key="img.id || img.url">
            <img class="gallery-img" :src="img.url" @error="img.broken = true" />
          </f7-swiper-slide>
        </f7-swiper>
        <div v-else class="gallery-placeholder">
          <f7-icon :f7="kindIcon" size="48" color="#8e8e93" />
        </div>
        <f7-link class="close-btn" icon-f7="xmark" sheet-close popup-close />
      </div>

      <f7-block class="no-margin-top detail-body">
        <!-- Kind + flags -->
        <div class="flag-row">
          <span class="kind-chip" :class="kind.toLowerCase()">
            <f7-icon :f7="kindIcon" size="10" /> {{ kindLabel }}
          </span>
          <span v-if="product.negotiable" class="negotiable-hint">{{ $t('marketplace.negotiable') }}</span>
          <span v-if="product.groupBuyEnabled" class="groupbuy-hint">
            <f7-icon f7="person_2_fill" size="10" /> {{ $t('marketplace.groupBuy') }}
          </span>
        </div>

        <h1 class="detail-name">{{ product.name }}</h1>

        <!-- Price block -->
        <template v-if="isMultiOffer">
          <div class="price-range">
            {{ $t('marketplace.from') }}
            <strong>{{ formatPrice(summary.min, summary.billingPeriod) }}</strong>
            <template v-if="summary.hasRange"> – {{ formatPrice(summary.max, summary.billingPeriod) }}</template>
          </div>
        </template>
        <template v-else>
          <div class="price-single">{{ formatPrice(primaryOffer.unitPrice, primaryOffer.billingPeriod) }}</div>
        </template>

        <!-- Availability -->
        <div class="availability-row" v-if="availabilityText">
          <f7-icon :f7="soldOut ? 'xmark_circle_fill' : 'checkmark_circle_fill'" size="12" :color="soldOut ? 'red' : 'green'" />
          {{ availabilityText }}
        </div>

        <p class="detail-desc" v-if="product.description">{{ product.description }}</p>

        <!-- Offer / plan selection -->
        <template v-if="isMultiOffer">
          <div class="section-label">
            {{ isSubscription ? $t('marketplace.choosePlan') : $t('marketplace.chooseOption') }}
          </div>
          <div class="offer-list">
            <label
              v-for="offer in offers"
              :key="offer.id"
              class="offer-card"
              :class="{ selected: selectedOfferId === offer.id }"
            >
              <input
                type="radio"
                name="offer-select"
                :value="offer.id"
                v-model="selectedOfferId"
                class="offer-radio"
              />
              <div class="offer-main">
                <div class="offer-name">
                  {{ offer.name || $t('marketplace.standardOffer') }}
                  <f7-icon v-if="offer.raw?.type === 'MEMBER'" f7="lock_fill" size="11" color="#0c447c" />
                </div>
                <div class="offer-desc" v-if="offer.description">{{ offer.description }}</div>
              </div>
              <div class="offer-price">
                <strong>{{ formatPrice(offer.unitPrice, offer.billingPeriod) }}</strong>
                <span v-if="memberCheaper(offer)" class="offer-strike">{{ formatPrice(memberCheaper(offer), 'ONE_TIME', false) }}</span>
              </div>
            </label>
          </div>
        </template>

        <!-- Quantity (physical only) -->
        <template v-if="isPhysical && !soldOut">
          <div class="section-label">{{ $t('common.quantity') }}</div>
          <div class="qty-row">
            <f7-stepper v-model:value="quantity" :min="1" :max="maxQuantity" raised />
            <span class="qty-total">{{ $t('common.total') }}: <strong>{{ formatPrice(selectedOffer.unitPrice * quantity, 'ONE_TIME') }}</strong></span>
          </div>
        </template>

        <!-- Kind-specific fulfillment info -->
        <div class="fulfilment-note" v-if="fulfilmentNote">
          <f7-icon :f7="fulfilmentIcon" size="12" />
          {{ fulfilmentNote }}
        </div>
      </f7-block>

      <!-- Sticky action bar -->
      <f7-toolbar bottom class="action-toolbar" v-if="!soldOut">
        <div class="toolbar-price">
          <span class="toolbar-price-label">{{ actionBarLabel }}</span>
          <strong>{{ formatPrice(selectedOffer.unitPrice * (isPhysical ? quantity : 1), summary.billingPeriod) }}</strong>
        </div>
        <div class="toolbar-actions">
          <f7-button v-if="isPhysical" outline large raised round @click="addToCart">{{ $t('marketplace.addToCart') }}</f7-button>
          <f7-button fill large round raised @click="buyNow">
            {{ primaryCta }}
          </f7-button>
        </div>
      </f7-toolbar>
    </f7-page>
  </AdaptiveSheet>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { f7 } from 'framework7-vue';
import AdaptiveSheet from '../common/AdaptiveSheet.vue';
import {
  PRODUCT_KINDS,
  productKind,
  normalizeOffers,
  summarizeOffers,
  totalStock,
  formatOfferPrice,
  BILLING_PERIODS,
} from '@/js/composables/useProductOffer.js';
import { usePaymentTrigger } from '@/js/composables/usePaymentTrigger.js';
import i18n from '@/js/i18n.js';

const t = i18n.global.t;

const props = defineProps({
  opened: { type: Boolean, default: false },
  /** { product, media, variants, priceRules } — same shape ProductCard emits upstream */
  item: { type: Object, default: null },
  shopName: { type: String, default: '' },
  shopId: { type: String, default: null },
  f7route: { type: Object, default: null },
  f7router: { type: Object, default: null },
});

const emit = defineEmits(['update:opened', 'closed', 'add-to-cart']);

const popupOpened = computed({
  get: () => props.opened,
  set: (value) => emit('update:opened', value),
});

const trigger = usePaymentTrigger({ f7router: props.f7router });

const quantity = ref(1);
const selectedOfferId = ref(null);

// ── Kind & offers ────────────────────────────────────────────────
const product = computed(() => props.item?.product || {});
const kind = computed(() => productKind(product.value));
const isPhysical = computed(() => kind.value === PRODUCT_KINDS.PHYSICAL);
const isSubscription = computed(() => kind.value === PRODUCT_KINDS.SUBSCRIPTION);

const KIND_META = {
  PHYSICAL: { icon: 'bag_fill', labelKey: 'marketplace.kindPhysical' },
  DIGITAL: { icon: 'cloud_fill', labelKey: 'marketplace.kindDigital' },
  SERVICE: { icon: 'wrench_fill', labelKey: 'marketplace.kindService' },
  SUBSCRIPTION: { icon: 'arrow_2_circlepath', labelKey: 'marketplace.kindSubscription' },
};
const kindIcon = computed(() => KIND_META[kind.value]?.icon || 'bag_fill');
const kindLabel = computed(() => t(KIND_META[kind.value]?.labelKey || 'marketplace.kindPhysical'));

const offers = computed(() => normalizeOffers(product.value, props.item?.variants || [], props.item?.priceRules || []));
const summary = computed(() => summarizeOffers(offers.value));
const isMultiOffer = computed(() => offers.value.length > 1);
const primaryOffer = computed(() => offers.value[0] || { unitPrice: 0, billingPeriod: 'ONE_TIME' });
const selectedOffer = computed(
  () => offers.value.find((o) => o.id === selectedOfferId.value) || primaryOffer.value
);

// Reset local state whenever the popup opens (or a new product is shown).
watch(
  [() => props.opened, () => props.item?.product?.id],
  ([isOpen]) => {
    if (!isOpen) return;
    quantity.value = 1;
    selectedOfferId.value = offers.value[0]?.id || null;
  },
  { immediate: true }
);

/** For MEMBER-priced offers show the crossed-out standard price. */
function memberCheaper(offer) {
  if (offer.raw?.type !== 'MEMBER') return null;
  const base = offers.value.find((o) => o !== offer && o.unitPrice > offer.unitPrice);
  return base ? base.unitPrice : null;
}

// ── Media ────────────────────────────────────────────────────────
const images = computed(() => (props.item?.media || []).filter((m) => m.mediaType === 'IMAGE'));

// ── Availability ────────────────────────────────────────────────
const stock = computed(() => totalStock(offers.value));
const soldOut = computed(() => isPhysical.value && stock.value === 0);
const maxQuantity = computed(() =>
  isPhysical.value && Number.isFinite(stock.value) && stock.value > 0 ? Math.min(stock.value, 99) : 99
);
const availabilityText = computed(() => {
  if (!isPhysical.value || stock.value == null) return '';
  if (stock.value === 0) return t('marketplace.outOfStock');
  if (stock.value <= 5) return t('marketplace.lowStock', { count: stock.value });
  return t('marketplace.inStock', { count: stock.value });
});

// ── Fulfillment note per kind ────────────────────────────────────
const fulfilmentIcon = computed(() => {
  switch (kind.value) {
    case PRODUCT_KINDS.DIGITAL: return 'arrow_down_doc_fill';
    case PRODUCT_KINDS.SERVICE: return 'calendar_badge_plus';
    case PRODUCT_KINDS.SUBSCRIPTION: return 'arrow_2_circlepath';
    default: return 'shippingbox_fill';
  }
});
const fulfilmentNote = computed(() => {
  switch (kind.value) {
    case PRODUCT_KINDS.DIGITAL:
      return t('marketplace.digitalDeliveryNote');
    case PRODUCT_KINDS.SERVICE:
      return t('marketplace.serviceBookingNote');
    case PRODUCT_KINDS.SUBSCRIPTION:
      return t('marketplace.subscriptionBillingNote', {
        period: t(BILLING_PERIODS[summary.value.billingPeriod]?.key || 'marketplace.periodOneTime'),
      });
    default:
      return product.value.fulfillmentType === 'PICKUP'
        ? t('shop.retraitBoutique')
        : t('marketplace.physicalDeliveryNote');
  }
});

// ── Actions ─────────────────────────────────────────────────────
const primaryCta = computed(() => {
  if (isSubscription.value) return t('marketplace.subscribe');
  if (kind.value === PRODUCT_KINDS.SERVICE) return t('marketplace.bookNow');
  if (isPhysical.value) return t('shopProduct.buyNow');
  return t('shopProduct.buyNow');
});

const actionBarLabel = computed(() => (isMultiOffer.value ? (selectedOffer.value.name || '') : ''));

function addToCart() {
  emit('add-to-cart', {
    product: product.value,
    offer: selectedOffer.value,
    quantity: quantity.value,
  });
}

/**
 * Hands the selection to the unified payment flow. Subscription/service
 * purchases pass the chosen offer as the priced option.
 */
function buyNow() {
  const offer = selectedOffer.value;
  try {
    trigger.triggerListingPurchase({
      product: {
        ...product.value,
        currentPrice: offer.unitPrice,
        currency: offer.currency || product.value.currency,
        name: isMultiOffer.value && offer.name ? `${product.value.name} — ${offer.name}` : product.value.name,
      },
      variant: offer.raw?.type ? offer.raw : { id: offer.id, name: offer.name, currentUnitPrice: offer.unitPrice },
      quantity: isPhysical.value ? quantity.value : 1,
      shopId: props.shopId || product.value.shopId,
      currency: offer.currency || product.value.currency,
    });
    popupOpened.value = false;
  } catch (err) {
    f7.dialog.alert(err?.message || t('common.paymentFailed'));
  }
}

function formatPrice(amount, billingPeriod = 'ONE_TIME', suffix = true) {
  return formatOfferPrice(amount, product.value.currency || 'XOF', suffix ? billingPeriod : 'ONE_TIME');
}

function onClosed() {
  emit('update:opened', false);
  emit('closed');
}
</script>

<style scoped>
.product-detail-page { --f7-navbar-height: 0px; }
.gallery-wrap { position: relative; background: var(--f7-list-bg-color, #f2f2f7); }
.gallery-img { width: 100%; height: 300px; object-fit: cover; display: block; }
.gallery-placeholder {
  height: 220px; display: flex; align-items: center; justify-content: center;
}
.close-btn {
  position: absolute; top: 10px; right: 10px; z-index: 10;
  width: 32px; height: 32px; border-radius: 50%;
  background: rgba(0, 0, 0, 0.45); color: #fff;
  display: flex; align-items: center; justify-content: center;
}
.detail-body { padding-bottom: 110px; }
.flag-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; flex-wrap: wrap; }
.kind-chip {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em;
  padding: 3px 8px; border-radius: 5px;
  background: var(--f7-list-bg-color, #f2f2f7); color: #555;
}
.kind-chip.digital { background: #e8f0fb; color: #0c447c; }
.kind-chip.service { background: #fdf3e4; color: #8a5a00; }
.kind-chip.subscription { background: #eaf3de; color: #27500a; }
.negotiable-hint, .groupbuy-hint { font-size: 11px; color: var(--f7-on-surface-variant, #666); display: inline-flex; align-items: center; gap: 3px; }
.detail-name { font-size: 20px; font-weight: 700; margin: 0 0 6px; line-height: 1.25; }
.price-single { font-size: 24px; font-weight: 700; color: var(--f7-theme-color); }
.price-range { font-size: 14px; color: var(--f7-on-surface-variant, #666); }
.price-range strong { font-size: 22px; font-weight: 700; color: var(--f7-theme-color); }
.availability-row { display: flex; align-items: center; gap: 5px; font-size: 12px; margin-top: 6px; color: var(--f7-text-color, #333); }
.detail-desc { font-size: 13px; line-height: 1.55; color: var(--f7-on-surface-variant, #555); white-space: pre-line; }
.section-label { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .05em; color: var(--f7-on-surface-variant, #8e8e93); margin: 16px 0 8px; }
.offer-list { display: flex; flex-direction: column; gap: 8px; }
.offer-card {
  display: flex; align-items: center; gap: 10px;
  border: 1.5px solid var(--f7-input-border-color, #dcdce0);
  border-radius: 12px; padding: 10px 12px; cursor: pointer;
  transition: border-color .15s ease, background .15s ease;
}
.offer-card.selected {
  border-color: var(--f7-theme-color);
  background: color-mix(in srgb, var(--f7-theme-color) 5%, transparent);
}
.offer-radio { accent-color: var(--f7-theme-color); flex-shrink: 0; }
.offer-main { flex: 1; min-width: 0; }
.offer-name { font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 4px; }
.offer-desc { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); margin-top: 2px; }
.offer-price { text-align: right; flex-shrink: 0; }
.offer-price strong { display: block; font-size: 14px; color: var(--f7-theme-color); }
.offer-strike { font-size: 10px; text-decoration: line-through; color: var(--f7-on-surface-variant, #999); }
.qty-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.qty-total { font-size: 13px; color: var(--f7-on-surface-variant, #666); }
.fulfilment-note {
  display: flex; align-items: center; gap: 6px;
  margin-top: 14px; padding: 9px 12px;
  background: var(--f7-list-bg-color, #f5f5f5); border-radius: 9px;
  font-size: 11.5px; color: var(--f7-on-surface-variant, #555);
}
.action-toolbar {
  --f7-toolbar-bg-color: var(--f7-sheet-bg-color, #fff);
  --f7-toolbar-height: 74px;
  padding: 0 14px;
}
.toolbar-price { display: flex; flex-direction: column; line-height: 1.25; min-width: 0; max-width: 40%; }
.toolbar-price-label {
  font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
}
.toolbar-price strong { font-size: 17px; }
.toolbar-actions { display: flex; gap: 8px; flex: 1; }
.toolbar-actions .button { flex: 1; }
</style>
